import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Select from "react-select";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const VENDOR_API =
  "https://q1xgsyocc2.execute-api.ap-south-1.amazonaws.com/Prod/vendors";

const vendorTypeOptions = [
  { label: "Supplier", value: "Supplier" },
  { label: "Service Provider", value: "Service Provider" },
  { label: "Contractor", value: "Contractor" },
  { label: "Consultant", value: "Consultant" },
];

function ManageVendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const today = new Date().toISOString().split("T")[0];
  const [isEditing, setIsEditing] = useState(false);
  const [editVendorId, setEditVendorId] = useState(null);

  const emptyForm = {
    vendorName: "",
    vendorType: "",
    contactPerson: "",
    phone: "",
    email: "",
    city: "",
    state: "",
    pincode: "",
    gstNo: "",
    panNo: "",
  };

  const [formData, setFormData] = useState(emptyForm);

  const resetForm = () => {
    setFormData(emptyForm);
    setIsEditing(false);
    setEditVendorId(null);
  };

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const response = await axios.get(VENDOR_API, {
        headers: { Authorization: `${localStorage.getItem("token")}` },
      });
      const data =
        response.data?.body?.data || response.data?.data || response.data || [];
      setVendors(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Failed to fetch vendors", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContractChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = [...prev.contracts];
      updated[index] = { ...updated[index], [name]: value };
      return { ...prev, contracts: updated };
    });
  };

  const handlePaymentChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = [...prev.payments];
      updated[index] = { ...updated[index], [name]: value };
      return { ...prev, payments: updated };
    });
  };

  const addContract = () => {
    setFormData((prev) => ({
      ...prev,
      contracts: [
        ...prev.contracts,
        { contractTitle: "", contractType: "", contractValue: "" },
      ],
    }));
  };

  const removeContract = (index) => {
    setFormData((prev) => ({
      ...prev,
      contracts: prev.contracts.filter((_, i) => i !== index),
    }));
  };

  const addPayment = () => {
    setFormData((prev) => ({
      ...prev,
      payments: [
        ...prev.payments,
        { invoiceNumber: "", amount: "", paymentMode: "" },
      ],
    }));
  };

  const removePayment = (index) => {
    setFormData((prev) => ({
      ...prev,
      payments: prev.payments.filter((_, i) => i !== index),
    }));
  };

  const handleEditClick = (vendor) => {
    setFormData({
      vendorName: vendor.vendorName || "",
      vendorType: vendor.vendorType || "",
      contactPerson: vendor.contactPerson || "",
      phone: vendor.phone || "",
      email: vendor.email || "",
      city: vendor.city || "",
      state: vendor.state || "",
      pincode: vendor.pincode || "",
      gstNo: vendor.gstNo || "",
      panNo: vendor.panNo || "",
    });
    setIsEditing(true);
    setEditVendorId(vendor.vendorId);
    const modal = new window.bootstrap.Modal(
      document.querySelector(".vendor-modal"),
    );
    modal.show();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEditing) {
        await axios.put(
          VENDOR_API,
          { ...formData, vendorId: editVendorId },
          {
            headers: { Authorization: `${localStorage.getItem("token")}` },
          },
        );
        toast.success("Vendor Updated", { position: "top-right" });
      } else {
        await axios.post(VENDOR_API, formData, {
          headers: { Authorization: `${localStorage.getItem("token")}` },
        });
        toast.success("Vendor Added", { position: "top-right" });
      }
      document.querySelector('[data-bs-dismiss="modal"]')?.click();
      fetchVendors();
      resetForm();
    } catch (error) {
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (vendorId, vendorName) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete vendor "${vendorName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${VENDOR_API}?vendorId=${vendorId}`, {
          headers: { Authorization: `${localStorage.getItem("token")}` },
        });
        toast.success("Vendor Deleted", { position: "top-right" });
        setVendors((prev) => prev.filter((v) => v.vendorId !== vendorId));
      } catch (error) {
        toast.error("Error occurred while deleting. Try again.");
      }
    }
  };

  const filteredRecords = vendors.filter((vendor) => {
    const searchLower = searchTerm.toLowerCase();
    return Object.values(vendor).some(
      (value) =>
        typeof value === "string" && value.toLowerCase().includes(searchLower),
    );
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleExportExcel = () => {
    if (!vendors.length) {
      toast.error("No vendor data to export");
      return;
    }
    const sheetData = vendors.map((v) => ({
      "Vendor ID": v.vendorId || "",
      "Vendor Name": v.vendorName || "",
      "Vendor Type": v.vendorType || "",
      "Contact Person": v.contactPerson || "",
      Phone: v.phone || "",
      Email: v.email || "",
      City: v.city || "",
      State: v.state || "",
      Pincode: v.pincode || "",
      "GST No.": v.gstNo || "",
      "PAN No.": v.panNo || "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vendors");
    XLSX.writeFile(workbook, `Vendors_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div
            className="modal fade vendor-modal"
            tabIndex="-1"
            role="dialog"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header d-flex justify-content-between">
                    <h5 className="modal-title fs-4">
                      {isEditing ? "Update Vendor" : "Add Vendor"}
                    </h5>
                    <div className="d-flex align-items-center">
                      <span className="badge bg-success-soft me-3 px-3">
                        Editable
                      </span>
                      <div className="form-check form-switch me-4">
                        <input
                          type="checkbox"
                          className="form-check-input m-0"
                          checked
                          readOnly
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      ></button>
                    </div>
                  </div>

                  <div className="modal-body">
                    <div className="row g-2 mb-2">
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Vendor Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="vendorName"
                          value={formData.vendorName}
                          onChange={handleChange}
                          placeholder="Enter Vendor Name"
                          required
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Vendor Type <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={vendorTypeOptions}
                          value={
                            vendorTypeOptions.find(
                              (o) => o.value === formData.vendorType,
                            ) || null
                          }
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              vendorType: selected?.value || "",
                            }))
                          }
                          placeholder="Select Type"
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Contact Person <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="contactPerson"
                          value={formData.contactPerson}
                          onChange={handleChange}
                          placeholder="Enter Contact Person"
                          required
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Phone <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Enter Phone"
                          required
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Email <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control text-dark"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter Email"
                          required
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">City</label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder="Enter City"
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">State</label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          placeholder="Enter State"
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">Pincode</label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          placeholder="Enter Pincode"
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">GST No.</label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="gstNo"
                          value={formData.gstNo}
                          onChange={handleChange}
                          placeholder="Enter GST No."
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">PAN No.</label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="panNo"
                          value={formData.panNo}
                          onChange={handleChange}
                          placeholder="Enter PAN No."
                        />
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer d-flex justify-content-between py-2">
                    <div>
                      <button
                        type="button"
                        className="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={loading}
                      >
                        {loading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="">
            <div className="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table className="table mb-0 text-nowrap table-centered">
                      <thead className="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((vendor, index) => (
                          <tr key={vendor.vendorId}>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Vendor ID</span>{" "}
                              &mdash; {vendor.vendorId}
                              <br />
                              <span className="text-dark">
                                Vendor Name
                              </span>{" "}
                              &mdash; {vendor.vendorName}
                              <br />
                              <span className="text-dark">
                                Vendor Type
                              </span>{" "}
                              &mdash; {vendor.vendorType}
                              <br />
                              <span className="text-dark">
                                Contact Person
                              </span>{" "}
                              &mdash; {vendor.contactPerson}
                              <br />
                              <span className="text-dark">
                                Phone
                              </span> &mdash; {vendor.phone}
                              <br />
                              <span className="text-dark">
                                Email
                              </span> &mdash; {vendor.email}
                              <br />
                              <span className="text-dark">
                                City
                              </span> &mdash; {vendor.city}
                              <br />
                              <span className="text-dark">
                                State
                              </span> &mdash; {vendor.state}
                              <br />
                              <span className="text-dark">
                                Pincode
                              </span> &mdash; {vendor.pincode}
                              <br />
                              <span className="text-dark">
                                GST No.
                              </span> &mdash; {vendor.gstNumber}
                              <br />
                              <span className="text-dark">
                                PAN No.
                              </span> &mdash; {vendor.panNumber}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span
                                  onClick={() => handleEditClick(vendor)}
                                  className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Update
                                </span>
                                <span
                                  onClick={() =>
                                    handleDelete(
                                      vendor.vendorId,
                                      vendor.vendorName,
                                    )
                                  }
                                  className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div>
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                        <Link
                          data-bs-toggle="modal"
                          onClick={resetForm}
                          data-bs-target=".vendor-modal"
                          className="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Add Vendor
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol className="breadcrumb pe-3">
                            <li className="breadcrumb-item">
                              <a href="#">Vendor Management</a>
                            </li>
                            <li
                              className="breadcrumb-item active"
                              aria-current="page"
                            >
                              Manage Vendors
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" alt="folder" />
                          )}
                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Manage Vendors
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              0.2 MB Size
                            </span>
                          </p>
                        </div>
                        <span
                          onClick={handleExportExcel}
                          className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                        >
                          Export
                        </span>
                      </div>

                      <div className="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#vendorReports"
                            role="button"
                            aria-expanded="false"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div className="collapse" id="vendorReports">
                            <div className="py-3">
                              <div className="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportExcel}
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  All Vendors
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>
                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);
                            if (currentPage > 3)
                              pageNumbers.push("left-ellipsis");
                            if (currentPage !== 1 && currentPage !== totalPages)
                              pageNumbers.push(currentPage);
                            if (currentPage < totalPages - 2)
                              pageNumbers.push("right-ellipsis");
                            if (totalPages > 1) pageNumbers.push(totalPages);
                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }
                              return (
                                <li
                                  key={index}
                                  className={`page-item ${currentPage === page ? "active" : ""}`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}
                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default ManageVendors;
